/**
 * Reusable marketing product page template (BR-005 blueprint).
 * Sections: hero → mockup → capabilities → how-it-works → integrations
 *           → proof → related products → final CTA.
 */
import { Link } from "react-router-dom";
import { ArrowRight, Play, Check, ChevronRight, ArrowUpRight, Sparkles } from "lucide-react";
import { MarketingHeader, MarketingFooter } from "./MarketingChrome";

const Kicker = ({ children }) => (
  <div className="text-xs uppercase tracking-[0.2em] text-emerald-500 font-semibold">
    {children}
  </div>
);

const Pill = ({ children }) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-xs font-semibold text-white">
    {children}
  </span>
);

const Section = ({ children, bg = "white", tid }) => {
  const bgClass = bg === "navy"
    ? "bg-[#0B1F3A] text-white"
    : bg === "slate"
    ? "bg-slate-50 text-slate-900"
    : "bg-white text-slate-900";
  return (
    <section data-testid={tid} className={bgClass}>
      <div className="max-w-7xl mx-auto px-6 py-20 md:py-24">{children}</div>
    </section>
  );
};

export const ProductScreen = ({ title, children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden">
    <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 flex items-center gap-2">
      <span className="w-2.5 h-2.5 rounded-full bg-red-400"></span>
      <span className="w-2.5 h-2.5 rounded-full bg-amber-400"></span>
      <span className="w-2.5 h-2.5 rounded-full bg-emerald-400"></span>
      <span className="ml-3 text-xs text-slate-500 font-mono truncate">{title}</span>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

export default function ProductPageTemplate({
  bp,
  eyebrow,
  title,
  titleAccent,
  subtitle,
  bullets = [],
  heroMockup,
  postHero,
  capabilities = [],
  howItWorks = [],
  integrations = [],
  proof,
  related = [],
  slug,
}) {
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid={`product-page-${slug}`}>
      <MarketingHeader />

      {/* HERO */}
      <section
        data-testid="product-hero"
        className="relative overflow-hidden bg-[#0B1F3A] text-white"
      >
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(5,150,105,0.22),transparent_60%)]"></div>
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-20 md:pt-24 md:pb-28 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <Pill>
              <Sparkles className="w-3 h-3 text-emerald-400" />
              {eyebrow}
            </Pill>
            <h1
              data-testid="product-hero-headline"
              className="mt-6 text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]"
            >
              {title}
              {titleAccent && (
                <>
                  <br />
                  <span className="text-emerald-400">{titleAccent}</span>
                </>
              )}
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-xl">{subtitle}</p>
            {bullets.length > 0 && (
              <ul className="mt-6 space-y-2 text-sm text-slate-300">
                {bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/auth?mode=signup"
                data-testid="product-cta-primary"
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-6 py-3"
              >
                Start Free Trial <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="product-cta-secondary"
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 hover:bg-white/5 text-white font-semibold px-6 py-3"
              >
                <Play className="w-4 h-4" /> Book Live Demo
              </Link>
            </div>
            {bp && (
              <div className="mt-8 text-xs font-mono text-slate-400">
                Powered by {bp}
              </div>
            )}
          </div>
          {heroMockup && (
            <div className="hidden md:block" data-testid="product-hero-mockup">
              {heroMockup}
            </div>
          )}
        </div>
      </section>

      {postHero}

      {/* CAPABILITIES */}
      {capabilities.length > 0 && (
        <Section tid="product-capabilities" bg="white">
          <Kicker>Core capabilities</Kicker>
          <h2 className="mt-2 text-3xl md:text-4xl font-bold max-w-3xl">
            What makes this different.
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-4">
            {capabilities.map(({ icon: Icon, title: t, desc }) => (
              <div
                key={t}
                className="rounded-xl border border-slate-200 p-5 hover:border-emerald-500 hover:shadow-lg transition-all"
              >
                {Icon && <Icon className="w-6 h-6 text-emerald-600" />}
                <div className="mt-3 font-bold text-lg">{t}</div>
                <div className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {desc}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* HOW IT WORKS */}
      {howItWorks.length > 0 && (
        <Section tid="product-how" bg="slate">
          <Kicker>How it works</Kicker>
          <h2 className="mt-2 text-3xl md:text-4xl font-bold max-w-3xl">
            Deterministic. Evidence-backed. Fast.
          </h2>
          <div className="mt-10 grid md:grid-cols-4 gap-4">
            {howItWorks.map(({ step, title: t, desc }, i) => (
              <div
                key={t}
                className="relative rounded-xl bg-white border border-slate-200 p-5"
              >
                <div className="text-3xl font-bold text-emerald-600">
                  {String(step ?? i + 1).padStart(2, "0")}
                </div>
                <div className="mt-2 font-bold">{t}</div>
                <div className="mt-1 text-sm text-slate-600">{desc}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* INTEGRATIONS / BP LINEAGE */}
      {integrations.length > 0 && (
        <Section tid="product-integrations" bg="white">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <Kicker>Cited engines</Kicker>
              <h2 className="mt-2 text-3xl md:text-4xl font-bold">
                Every insight cites its source.
              </h2>
              <p className="mt-3 text-slate-600 max-w-2xl">
                Evidence traceability is baked in. Every metric shown on this
                product references the deterministic engine (with version) that
                produced it — no black boxes.
              </p>
            </div>
          </div>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
            {integrations.map(({ bp: b, name }) => (
              <div
                key={b}
                className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3"
              >
                <div className="text-[10px] font-mono font-bold text-emerald-600">
                  {b}
                </div>
                <div className="text-sm font-semibold text-slate-900">
                  {name}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* PROOF */}
      {proof && (
        <Section tid="product-proof" bg="navy">
          <div className="grid md:grid-cols-3 gap-8 items-center">
            <div>
              <div className="text-6xl md:text-7xl font-bold text-emerald-400">
                {proof.stat}
              </div>
              <div className="mt-2 text-sm uppercase tracking-widest text-slate-300">
                {proof.label}
              </div>
            </div>
            <div className="md:col-span-2">
              <div className="text-2xl md:text-3xl font-serif italic text-white leading-snug">
                &ldquo;{proof.quote}&rdquo;
              </div>
              <div className="mt-4 text-sm text-slate-400">— {proof.attribution}</div>
            </div>
          </div>
        </Section>
      )}

      {/* RELATED */}
      {related.length > 0 && (
        <Section tid="product-related" bg="slate">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <Kicker>Better together</Kicker>
              <h2 className="mt-2 text-3xl md:text-4xl font-bold">
                Pairs with the rest of the platform.
              </h2>
            </div>
            <Link
              to="/products"
              className="text-emerald-600 font-semibold flex items-center gap-1 hover:underline"
            >
              Explore all products <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            {related.map(({ title: t, desc, to }) => (
              <Link
                key={t}
                to={to}
                data-testid={`related-${to.split("/").pop()}`}
                className="group rounded-xl border border-slate-200 bg-white p-5 hover:border-emerald-500 hover:shadow-lg transition-all"
              >
                <div className="font-bold text-lg group-hover:text-emerald-600">
                  {t}
                </div>
                <div className="mt-2 text-sm text-slate-600">{desc}</div>
                <div className="mt-3 text-emerald-600 text-sm font-semibold inline-flex items-center gap-1">
                  Learn more <ArrowUpRight className="w-3.5 h-3.5" />
                </div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* FINAL CTA */}
      <section
        data-testid="product-final-cta"
        className="relative overflow-hidden bg-[#0B1F3A] text-white"
      >
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(5,150,105,0.22),transparent_70%)]"></div>
        <div className="relative max-w-4xl mx-auto px-6 py-20 md:py-24 text-center">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
            Speed is the deal.<br />
            <span className="text-emerald-400">Start closing faster today.</span>
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/auth?mode=signup"
              data-testid="product-final-cta-primary"
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-6 py-3"
            >
              Start Free Trial <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-lg border border-white/20 hover:bg-white/5 text-white font-semibold px-6 py-3"
            >
              Book a live demo
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
